import type { SituacaoDoInventario, TipoDaDiferenca } from '../../api/inventarios.js';
import { contagemDoRecorte } from '../../api/paginacao.js';
import type { TomStatus } from '../../componentes/ChipStatus.js';

/**
 * Como o inventário se lê na tela: rótulos, tons e contagens em português.
 *
 * Aqui só mora apresentação. O dado vem do middleware (`api/inventarios.ts`),
 * e a situação já vem derivada de lá — este arquivo escolhe a palavra e a cor,
 * nunca o significado. Antes tudo isto vivia em `dados-exemplo/inventarios.ts`,
 * ao lado de treze contagens que nunca aconteceram.
 */

/** Rótulo e tom de cada situação — cor sozinha nunca comunica estado (RNF-022). */
export const CHIP_INVENTARIO: Readonly<
  Record<SituacaoDoInventario, { readonly tom: TomStatus; readonly rotulo: string }>
> = {
  conflito: { tom: 'falha', rotulo: 'Conflito de contagem' },
  aguardandoRevisao: { tom: 'divergencia', rotulo: 'Aguardando revisão' },
  emContagem: { tom: 'andamento', rotulo: 'Em contagem' },
  resolvido: { tom: 'concluida', rotulo: 'Resolvido' },
};

/** Falta, sobra e conferido — o que o sinal da diferença significa no chão. */
export const ROTULO_DA_DIFERENCA: Readonly<Record<TipoDaDiferenca, string>> = {
  falta: 'Falta',
  sobra: 'Sobra',
  conferido: 'Conferido',
};

/** Contagem do recorte, no gênero certo. A regra é a de `api/paginacao.ts`. */
export function contagemDeCiclos(total: number): string {
  return contagemDoRecorte(total, 'ciclo', 'ciclos');
}

/** O selo do cabeçalho conta CICLOS, não divergências — são coisas diferentes. */
export function contagemDeCiclosComDivergencia(total: number): string {
  return contagemDoRecorte(
    total,
    'ciclo com divergência em aberto',
    'ciclos com divergência em aberto',
  );
}

/**
 * Quantos SKUs a contagem mais recente teve.
 *
 * Nulo é "ninguém contou ainda", e não zero: um depósito contado e vazio e um
 * depósito que ninguém visitou são fatos diferentes, e a coluna precisa
 * distingui-los.
 */
export function skusContados(total: number | null): string {
  return total === null ? '—' : `${String(total)} ${total === 1 ? 'SKU' : 'SKUs'}`;
}

const NUMERO_POR_EXTENSO: readonly string[] = ['nenhuma', 'uma', 'duas', 'três', 'quatro', 'cinco'];

/** "duas contagens" lê melhor que "2 contagens" numa frase de aviso. */
export function quantidadeDeContagens(total: number): string {
  const extenso = NUMERO_POR_EXTENSO[total] ?? String(total);
  return `${extenso} ${total === 1 ? 'contagem' : 'contagens'}`;
}
